// The environment overflow menu: Copy…, SDK keys, favorites, Edit environment,
// plus any route-provided section (`handle.environmentSettingsMenuItems`, e.g.
// the flag list's "Compare to another environment"). Shared by the toolbar
// environment chips (EnvironmentSelector) and the flag list's environment
// column headers (FlagListNext). Callers own the MenuTrigger + Popover;
// caller-specific items render after the shared sections via `children`.

import type { ReactNode } from 'react';
import { Text } from 'react-aria-components';
import { isSandbox } from '@gonfalon/constants/constants';
import { concurrentSdkKeysFe } from '@gonfalon/dogfood-flags/dogfood-flags';
import { copyToClipboard } from '@gonfalon/dom/copyToClipboard';
import { isAgentControlSandboxEnvironment } from '@gonfalon/environments/isAgentControlSandboxEnvironment';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toEditProjectEnvironment, toHref, toSdkKeys } from '@gonfalon/navigator/navigators';
import { useUpdateFollowPreferences } from '@gonfalon/rest-api/hooks/useUpdateFollowPreferences';
import type { JSONPatch } from '@gonfalon/rest-api/types';
import { trackSdkKeyCopied } from '@gonfalon/sdk-keys/analytics';
import { Header, Menu, MenuItem, MenuSection, snackbarQueue, toastQueue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { z } from 'zod';

import { useFavoriteEnvironmentIds } from '../EnvironmentController/useFavoriteEnvironmentIds';
import { type SelectedEnvironment } from '../types';
import { useEnvironmentMenuItems } from '../useEnvironmentSettingsMenuItem';

import styles from './EnvironmentSelector.module.css';

const configurationActionSchema = z.enum(['copy-key', 'copy-sdk', 'copy-mobile', 'copy-client-side', 'favorite']);

function isPredefinedConfigurationAction(action: unknown): action is z.infer<typeof configurationActionSchema> {
  return configurationActionSchema.safeParse(action).success;
}

export function EnvironmentActionsMenu({
  environment,
  projectKey,
  children,
}: {
  environment: SelectedEnvironment;
  projectKey: string;
  children?: ReactNode;
}) {
  // Every item this menu owns is a dead end in a sandbox session: the keys aren't
  // real, and favorites and Edit environment are mutations. They're omitted rather
  // than disabled, for two reasons [[REL-15386]]:
  //
  //  - Callers must not disable the whole trigger instead. The flag list nests its
  //    own still-usable actions (swap, move) here via `children`, and gating at the
  //    trigger took those down with it — the bug this replaced.
  //  - `isDisabled` alone isn't enough for the two link items. It suppresses the
  //    press but leaves the anchor, and an absent `href` resolves to the current
  //    path through the router integration rather than being dropped — so a
  //    disabled item stays ⌘-clickable. Not rendering it sidesteps that entirely.
  //
  // Caller-provided and route-provided items are left alone; those callers know
  // what they permit.
  const inSandbox = isSandbox();
  const hideSdkKeys = isAgentControlSandboxEnvironment(environment);
  // Pass this menu's environment so route-provided items (e.g. "Compare to
  // another environment") target it, not just the selected environment.
  const settingsMenuItems = useEnvironmentMenuItems(environment.key);
  const favorites = useFavoriteEnvironmentIds();
  const isFavorite = favorites.includes(environment._id);

  const { mutate } = useUpdateFollowPreferences();

  const updateFavorite = (instructions: JSONPatch) =>
    mutate(
      { body: instructions },
      {
        onSuccess() {
          toastQueue.add({ title: isFavorite ? 'Removed favorite' : 'Added favorite', status: 'success' });
        },
        onError() {
          snackbarQueue.add({ description: 'Failed to update tags', status: 'error' });
        },
      },
    );

  return (
    <Menu
      onAction={async (action) => {
        if (!isPredefinedConfigurationAction(action)) {
          return;
        }

        switch (action) {
          case 'favorite':
            if (isFavorite) {
              const found: number[] = [];
              for (const [index, id] of favorites.entries()) {
                if (id === environment._id) {
                  found.push(index);
                }
              }

              if (found.length === 0) {
                return;
              }

              const instructions = [];
              for (const index of found) {
                instructions.push({
                  op: 'remove',
                  path: `/resources/${index}`,
                } as const);
              }

              updateFavorite(instructions);
            } else {
              if (favorites.findIndex((value) => environment._id === value) !== -1) {
                return;
              }

              updateFavorite([
                {
                  op: 'add',
                  path: '/resources/-',
                  value: { id: environment._id, resourceKind: 'environment' },
                },
              ]);
            }
            break;
          default:
            trackCopyAction(action);
            await copyKey(action, environment);
            break;
        }
      }}
    >
      {!inSandbox && (
        <MenuSection>
          <Header>
            <Message id="chrome.environmentActions.copyMenu" />
          </Header>
          <MenuItem id="copy-key">
            <Text slot="label">{msg('chrome.environmentActions.environmentKey')}</Text>
          </MenuItem>

          {!concurrentSdkKeysFe() && !hideSdkKeys && (
            <>
              <MenuItem id="copy-sdk">
                <Text slot="label">{msg('chrome.environmentActions.sdkKey')}</Text>
              </MenuItem>
              <MenuItem id="copy-mobile">
                <Text slot="label">{msg('chrome.environmentActions.mobileKey')}</Text>
              </MenuItem>
              <MenuItem id="copy-client-side">
                <Text slot="label">{msg('chrome.environmentActions.clientSideId')}</Text>
              </MenuItem>
            </>
          )}
        </MenuSection>
      )}

      {!inSandbox && !hideSdkKeys && concurrentSdkKeysFe() && (
        <MenuSection>
          <MenuItem
            id="to-sdk-keys"
            href={toHref(
              toSdkKeys({
                search: new URLSearchParams({ projKey: projectKey, envKey: environment.key }),
              }),
            )}
          >
            <Text slot="label">
              <Icon name="key" size="small" />
              {msg('chrome.environmentActions.viewSdkKeys')}
            </Text>
          </MenuItem>
        </MenuSection>
      )}

      {!inSandbox && (
        <MenuSection>
          <MenuItem id="favorite" className={styles.favoriteItem} data-favorite={isFavorite ? true : undefined}>
            <Text slot="label">
              {isFavorite ? (
                <>
                  <Icon name="star" size="small" />
                  {msg('chrome.environmentActions.removeFromFavorites')}
                </>
              ) : (
                <>
                  <Icon name="star-outline" size="small" />
                  {msg('chrome.environmentActions.addToFavorites')}
                </>
              )}
            </Text>
          </MenuItem>
        </MenuSection>
      )}
      {!inSandbox && (
        <MenuSection>
          <MenuItem
            id="edit-environment-entity"
            href={toHref(
              toEditProjectEnvironment({
                projectKey,
                environmentKey: environment.key,
              }),
            )}
          >
            <Text slot="label">{msg('chrome.environmentActions.editEnvironment')}</Text>
          </MenuItem>
        </MenuSection>
      )}
      {settingsMenuItems !== undefined ? (
        <MenuSection>
          <Header>{settingsMenuItems.header}</Header>
          {settingsMenuItems.items.map((menuItem) => (
            <MenuItem
              key={menuItem.label}
              id={menuItem.label}
              href={menuItem.to ? toHref(menuItem.to) : undefined}
              onAction={menuItem.onAction}
            >
              <Text slot="label">{menuItem.label}</Text>
            </MenuItem>
          ))}
        </MenuSection>
      ) : null}
      {children}
    </Menu>
  );
}

async function copyKey(action: z.infer<typeof configurationActionSchema>, item: SelectedEnvironment) {
  if (action === 'copy-key') {
    await copyToClipboard(item.key, 'Environment key copied to clipboard');
    return;
  }

  let key: string;
  if (action === 'copy-client-side') {
    key = item._id;
  } else if (action === 'copy-mobile') {
    key = item.mobileKey;
  } else if (action === 'copy-sdk') {
    key = item.apiKey;
  } else {
    return;
  }

  await copyToClipboard(key, 'SDK key copied to clipboard');
}

function trackCopyAction(action: z.infer<typeof configurationActionSchema>) {
  switch (action) {
    case 'copy-key':
      trackSdkKeyCopied({ source: 'Environment Key' });
      break;
    case 'copy-sdk':
      trackSdkKeyCopied({ source: 'SDK Key' });
      break;
    case 'copy-mobile':
      trackSdkKeyCopied({ source: 'Mobile Key' });
      break;
    case 'copy-client-side':
      trackSdkKeyCopied({ source: 'Client-side ID' });
      break;
    default:
      break;
  }
}
